import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { LogoFull } from "@/components/Logo";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#why-us" },
  { label: "Process", href: "#process" },
  { label: "Coverage", href: "#coverage" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);

  const anchor = (href) => (e) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(href);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className="fixed top-0 inset-x-0 z-50"
      data-testid="main-nav"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <div className="mt-4 flex items-center justify-between rounded-2xl border border-white/10 bg-[#0A0B0E]/70 px-4 py-3 backdrop-blur-xl">
          <a
            href="#top"
            onClick={anchor("#top")}
            className="shrink-0"
            data-testid="nav-logo-link"
            aria-label="Red Star Solutions home"
          >
            <LogoFull />
          </a>

          <nav className="hidden lg:flex items-center gap-7">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={anchor(l.href)}
                className="font-jbmono text-xs uppercase tracking-[0.18em] text-slate-400 transition-colors hover:text-white"
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.label}
              </a>
            ))}
            <Link
              to="/admin"
              className="font-jbmono text-xs uppercase tracking-[0.18em] text-slate-400 transition-colors hover:text-white"
              data-testid="nav-admin-button"
            >
              Admin
            </Link>
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:+15137205882"
              className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-slate-300 transition-colors hover:border-[#FF2A3B]/50 hover:text-white"
              data-testid="nav-phone-link"
            >
              <Phone size={14} className="text-[#FF2A3B]" />
              (513) 720-5882
            </a>
            <a
              href="#booking"
              onClick={anchor("#booking")}
              className="rounded-full bg-[#FF2A3B] px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-[#E01F30] hover:shadow-[0_0_30px_rgba(255,42,59,0.4)]"
              data-testid="nav-book-button"
            >
              Book Service
            </a>
          </div>

          <button
            className="lg:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden mt-2 rounded-2xl border border-white/10 bg-[#0A0B0E]/95 backdrop-blur-xl p-4 space-y-1" data-testid="nav-mobile-menu">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={anchor(l.href)}
                className="block w-full text-left px-3 py-3 rounded-lg text-slate-300 hover:bg-white/5"
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.label}
              </a>
            ))}
            <Link
              to="/admin"
              className="block px-3 py-3 rounded-lg text-slate-300 hover:bg-white/5"
              data-testid="nav-mobile-admin-link"
            >
              Admin
            </Link>
            <a
              href="#booking"
              onClick={anchor("#booking")}
              className="block w-full rounded-full bg-[#FF2A3B] px-5 py-3 text-center text-sm font-semibold text-white"
              data-testid="nav-mobile-book-button"
            >
              Book Service
            </a>
          </div>
        )}
      </div>
    </motion.header>
  );
};
